// كشف حساب — بالتصميم الموحّد
// يقبل روابط مباشرة: ?accountId=12&from=2026-01-01&to=2026-10-25 (يستخدمها ميزان المراجعة)
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { DataGrid } from '@mui/x-data-grid';
import { toast } from 'react-toastify';
import { Autocomplete, TextField, Box, Typography, Alert } from '@mui/material';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import TagIcon from '@mui/icons-material/Tag';

import accountingReportsApi from '../../api/accountingReportsApi';
import chartOfAccountsApi from '../../api/chartOfAccountsApi';
import {
  ReportPage, ReportFilters, ReportTableCard, useReportDateRange, reportGridSx, fieldSx, fmtMoney, fmtNumber,
} from '../../components/Reports/ReportKit';
import { useReportPrint, useCompanyInfo, fmtDate } from '../../components/Reports/PrintKit';
import AccountStatementPrint from './AccountStatementPrint';

// أسماء أنواع القيود إذا رجعت من الـ API كرقم
const ENTRY_TYPE_NAMES = {
  1: 'قيد يومية', 2: 'قيد افتتاحي', 3: 'مبيعات', 4: 'مرتجع مبيعات', 5: 'مشتريات', 6: 'مرتجع مشتريات',
  7: 'سند صرف', 8: 'سند قبض', 9: 'تحويل', 10: 'تسوية', 11: 'إهلاك', 12: 'رواتب', 13: 'إقفال',
};
export const entryTypeName = (v) => (typeof v === 'number' || /^\d+$/.test(String(v)) ? ENTRY_TYPE_NAMES[Number(v)] || v : v);

// الرصيد: موجب = مدين، سالب = دائن
export const balanceLabel = (v) => {
  const n = Number(v) || 0;
  if (Math.abs(n) < 0.005) return '0.00';
  return `${fmtMoney(Math.abs(n))} ${n > 0 ? 'مدين' : 'دائن'}`;
};

export default function AccountStatement() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const range = useReportDateRange('today');
  const company = useCompanyInfo();

  const [accounts, setAccounts] = useState([]);
  const [account, setAccount] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  // تطبيق الفترة من الرابط (مرة واحدة)
  const appliedParams = useRef(false);
  useEffect(() => {
    if (appliedParams.current) return;
    appliedParams.current = true;
    const from = params.get('from');
    const to = params.get('to');
    if (from) range.setDateFrom(from);
    if (to) range.setDateTo(to);
  }, [params, range]);

  useEffect(() => {
    chartOfAccountsApi.getAll({ includeInactive: false })
      .then(res => {
        const list = res.data || [];
        setAccounts(list);
        const id = params.get('accountId');
        if (id) setAccount(list.find(a => String(a.id) === id) || null);
      })
      .catch(() => toast.error('فشل تحميل الحسابات'));
    // مرة واحدة عند الفتح
  }, []);

  const fetchStatement = useCallback(async () => {
    if (!account) { setData(null); return; }
    setLoading(true);
    try {
      const res = await accountingReportsApi.getStatement({
        accountId: account.id, fromDate: range.dateFrom, toDate: range.dateTo,
      });
      setData(res.data);
    } catch (err) {
      console.error(err);
      toast.error('فشل جلب كشف الحساب');
    } finally {
      setLoading(false);
    }
  }, [account, range.dateFrom, range.dateTo]);

  useEffect(() => {
    const id = setTimeout(fetchStatement, 300);
    return () => clearTimeout(id);
  }, [fetchStatement]);

  const onAccountChange = (_, v) => {
    setAccount(v);
    const next = new URLSearchParams(params);
    if (v) next.set('accountId', v.id); else next.delete('accountId');
    setParams(next, { replace: true });
  };

  // صف الرصيد الافتتاحي + الحركات (بالترتيب الزمني من السيرفر)
  const rows = useMemo(() => {
    if (!data) return [];
    const opening = Number(data.openingBalance) || 0;
    return [
      { id: '__opening', opening: true, date: range.dateFrom, description: 'رصيد افتتاحي (ما قبل الفترة)',
        debit: opening > 0 ? opening : 0, credit: opening < 0 ? -opening : 0, balance: opening },
      ...(data.lines || []).map((l, i) => ({ ...l, id: l.id ?? `l${i}` })),
    ];
  }, [data, range.dateFrom]);

  const printRef = useRef(null);
  const handlePrint = useReportPrint(printRef, `كشف-حساب-${account?.code || ''}`);

  const num = (color) => ({
    type: 'number', width: 135, sortable: false, headerAlign: 'center',
    renderCell: p => (
      <Typography sx={{ fontSize: 14, fontWeight: p.value ? 700 : 400, color: p.value ? color : '#cbd5e1', direction: 'ltr' }}>
        {p.value ? fmtMoney(p.value) : '—'}
      </Typography>
    ),
  });

  const columns = [
    {
      field: 'entryNumber', headerName: 'رقم القيد', width: 100, sortable: false,
      renderCell: p => p.row.opening ? <Typography sx={{ color: '#94a3b8' }}>—</Typography> : (
        p.row.journalEntryGlobalId
          ? <Typography component="a" onClick={() => navigate(`/journal-entries/${p.row.journalEntryGlobalId}/view`)}
              sx={{ fontWeight: 800, color: '#4f46e5', cursor: 'pointer', textDecoration: 'underline' }}>{p.value}</Typography>
          : <Typography sx={{ fontWeight: 800 }}>{p.value}</Typography>
      ),
    },
    { field: 'date', headerName: 'التاريخ', width: 110, sortable: false, renderCell: p => fmtDate(p.value) },
    {
      field: 'entryType', headerName: 'نوع القيد', width: 130, sortable: false,
      renderCell: p => p.row.opening ? '—' : (
        <Box sx={{ px: 1, py: .3, borderRadius: '999px', fontSize: 12.5, fontWeight: 700, bgcolor: '#eef2ff', color: '#4338ca' }}>
          {entryTypeName(p.value) || '—'}
        </Box>
      ),
    },
    {
      field: 'description', headerName: 'البيان', flex: 1, minWidth: 240, sortable: false,
      renderCell: p => (
        <Typography sx={{ fontSize: 13.5, fontWeight: p.row.opening ? 800 : 400, width: '100%', textAlign: 'right',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={p.value || ''}>
          {p.value || '—'}
        </Typography>
      ),
    },
    { field: 'debit', headerName: 'مدين', ...num('#0369a1') },
    { field: 'credit', headerName: 'دائن', ...num('#047857') },
    {
      field: 'balance', headerName: 'الرصيد', width: 160, sortable: false, headerAlign: 'center',
      renderCell: p => (
        <Typography sx={{ fontSize: 14, fontWeight: 800, color: Number(p.value) < 0 ? '#be123c' : '#1e293b' }}>
          {balanceLabel(p.value)}
        </Typography>
      ),
    },
  ];

  const opening = Number(data?.openingBalance) || 0;
  const closing = Number(data?.closingBalance) || 0;

  return (
    <ReportPage
      title="كشف حساب"
      subtitle={account ? `${account.code} — ${account.nameAr}` : 'اختر الحساب لعرض حركته خلال الفترة'}
      icon={<ReceiptLongOutlinedIcon />}
      period={range}
      badges={account ? [{ label: account.allowPosting ? 'حساب فرعي' : 'حساب رئيسي (مجمّع)', icon: <TagIcon /> }] : []}
      actions={[
        { label: 'تحديث', icon: <RefreshIcon />, onClick: fetchStatement, disabled: loading || !account },
        { label: 'طباعة', icon: <PrintOutlinedIcon />, onClick: handlePrint, disabled: !data, primary: true },
      ]}
      stats={data ? [
        { label: 'رصيد افتتاحي', value: balanceLabel(opening), icon: <AccountBalanceIcon />, color: 'slate' },
        { label: 'إجمالي مدين', value: fmtMoney(data.totalDebit), icon: <TrendingUpIcon />, color: 'info' },
        { label: 'إجمالي دائن', value: fmtMoney(data.totalCredit), icon: <TrendingDownIcon />, color: 'success' },
        { label: 'رصيد ختامي', value: balanceLabel(closing), icon: <AccountBalanceWalletIcon />, color: closing < 0 ? 'error' : 'primary', highlight: true },
      ] : []}
      filters={
        <ReportFilters
          range={range}
          onApply={fetchStatement}
          onReset={() => range.reset()}
          loading={loading}
          activeCount={account ? 1 : 0}
        >
          <Autocomplete
            size="small"
            options={accounts}
            value={account}
            onChange={onAccountChange}
            getOptionLabel={o => (o ? `${o.code} - ${o.nameAr}` : '')}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            noOptionsText="لا توجد حسابات"
            sx={{ minWidth: { md: 320 } }}
            renderOption={(props, o) => (
              <li {...props} key={o.id}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: 14, fontWeight: 700 }}>{o.code} - {o.nameAr}</Typography>
                  <Typography sx={{ fontSize: 12, color: '#64748b' }}>
                    {o.accountTypeText ? `${o.accountTypeText} · ` : ''}{o.allowPosting ? 'يقبل القيد' : 'رئيسي'}
                  </Typography>
                </Box>
              </li>
            )}
            renderInput={p => <TextField {...p} label="الحساب" placeholder="ابحث بالرقم أو الاسم" sx={fieldSx} />}
          />
        </ReportFilters>
      }
    >
      <Box sx={{ display: 'none' }}>
        <AccountStatementPrint ref={printRef} reportData={data} fromDate={range.dateFrom} toDate={range.dateTo} company={company} />
      </Box>

      {!account ? (
        <Alert severity="info" sx={{ borderRadius: '12px' }}>اختر الحساب من الفلاتر لعرض الكشف.</Alert>
      ) : (
        <ReportTableCard title={`حركة الحساب — ${account.nameAr}`} count={fmtNumber(data?.lines?.length || 0)}>
          <DataGrid
            rows={rows}
            columns={columns}
            loading={loading}
            disableRowSelectionOnClick
            disableColumnMenu
            getRowHeight={() => 48}
            getRowClassName={p => (p.row.opening ? 'row-opening' : '')}
            initialState={{ pagination: { paginationModel: { pageSize: 100 } } }}
            pageSizeOptions={[50, 100, 500]}
            localeText={{ noRowsLabel: 'لا توجد حركات خلال الفترة' }}
            sx={{ ...reportGridSx, '& .row-opening': { bgcolor: '#fff7ed !important' } }}
          />
        </ReportTableCard>
      )}
    </ReportPage>
  );
}
